import axios from "axios";
import config from "../api_config.json";

const SEARCH_RESULT_FIELDS = `
  objectId
  nome
  stato
  tipo_istanza
`.replace(/\r?\n|\r/g, " ").replace(/\s+/g, " ").trim();

const SEARCH_ORGS_BY_NAME_QUERY = `
query SearchOrganizationsByName($first: Int!, $searchTerm: String!, $order: [OrganizzazioneOrder!]) {
  organizzaziones(
    first: $first,
    where: { nome: { matchesRegex: $searchTerm, options: "i" } },
    order: $order
  ) {
    edges {
      node {
        ${SEARCH_RESULT_FIELDS}
      }
    }
  }
}`.replace(/\r?\n|\r/g, " ").replace(/\s+/g, " ").trim();

const GET_ORG_BY_ID_QUERY = `
query GetOrgById($id: ID!) {
  organizzazione(id: $id) {
    ${SEARCH_RESULT_FIELDS}
  }
}`.replace(/\r?\n|\r/g, " ").replace(/\s+/g, " ").trim();

const SEARCH_REST_KEYS = "objectId,nome,stato,tipo_istanza";
const OBJECT_ID_SEARCH_RE = /^[A-Za-z0-9]+$/;
const EXACT_OBJECT_ID_MIN_LENGTH = 8;
const PARTIAL_OBJECT_ID_MIN_LENGTH = 3;

const getHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const getBaseUrl = (env) => config.baseUrl[env];
const getGraphqlUrl = (env) => `${getBaseUrl(env)}/graphql?raw=true`;

export const escapeRegex = (value = "") =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const isObjectIdSearchCandidate = (value = "") =>
  OBJECT_ID_SEARCH_RE.test(value);

const canAttemptExactObjectIdLookup = (value = "") =>
  isObjectIdSearchCandidate(value) && value.length >= EXACT_OBJECT_ID_MIN_LENGTH;

const canAttemptPartialObjectIdLookup = (value = "") =>
  isObjectIdSearchCandidate(value) && value.length >= PARTIAL_OBJECT_ID_MIN_LENGTH;

const normalizeOrg = (org) => {
  if (!org?.objectId) {
    return null;
  }

  return {
    id: org.objectId,
    name: org.nome,
    stato: org.stato,
    tipo: org.tipo_istanza,
  };
};

const mergeOrganizations = (groups, limit) => {
  const merged = [];
  const seenIds = new Set();

  groups.flat().forEach((org) => {
    if (!org?.id || seenIds.has(org.id)) {
      return;
    }

    seenIds.add(org.id);
    merged.push(org);
  });

  return typeof limit === "number" ? merged.slice(0, limit) : merged;
};

const fetchOrgById = async ({ env, token, searchTerm }) => {
  if (!canAttemptExactObjectIdLookup(searchTerm)) {
    return null;
  }

  try {
    const response = await axios.get(
      `${getBaseUrl(env)}/classes/organizzazione/${encodeURIComponent(searchTerm)}`,
      {
        headers: getHeaders(token),
        params: {
          keys: SEARCH_REST_KEYS,
        },
      }
    );

    return normalizeOrg(response.data);
  } catch (restError) {
    try {
      const response = await axios({
        method: "post",
        url: getGraphqlUrl(env),
        data: {
          query: GET_ORG_BY_ID_QUERY,
          variables: { id: searchTerm },
        },
        headers: getHeaders(token),
      });

      return normalizeOrg(response.data?.organizzazione);
    } catch (graphqlError) {
      return null;
    }
  }
};

const fetchOrgsByObjectIdPrefix = async ({ env, token, searchTerm, limit }) => {
  if (!canAttemptPartialObjectIdLookup(searchTerm)) {
    return [];
  }

  try {
    const response = await axios.get(`${getBaseUrl(env)}/classes/organizzazione`, {
      headers: getHeaders(token),
      params: {
        keys: SEARCH_REST_KEYS,
        limit,
        order: "nome",
        where: JSON.stringify({
          objectId: {
            $regex: `^${escapeRegex(searchTerm)}`,
            $options: "i",
          },
        }),
      },
    });

    return (response.data?.results || []).map(normalizeOrg).filter(Boolean);
  } catch (error) {
    return [];
  }
};

const fetchOrgsByName = async ({ env, token, searchTerm, limit }) => {
  try {
    const response = await axios({
      method: "post",
      url: getGraphqlUrl(env),
      data: {
        query: SEARCH_ORGS_BY_NAME_QUERY,
        variables: {
          first: limit,
          searchTerm: escapeRegex(searchTerm),
          order: ["nome_ASC"],
        },
      },
      headers: getHeaders(token),
    });

    const edges = response.data?.organizzaziones?.edges || [];
    return edges.map((edge) => normalizeOrg(edge.node)).filter(Boolean);
  } catch (error) {
    return [];
  }
};

export const searchOrganizations = async ({
  env,
  token,
  searchTerm,
  limit = 8,
}) => {
  const query = searchTerm.trim();

  if (!query) {
    return [];
  }

  const [exactMatch, objectIdMatches, nameMatches] = await Promise.all([
    fetchOrgById({ env, token, searchTerm: query }),
    fetchOrgsByObjectIdPrefix({ env, token, searchTerm: query, limit }),
    fetchOrgsByName({ env, token, searchTerm: query, limit }),
  ]);

  return mergeOrganizations(
    [exactMatch ? [exactMatch] : [], objectIdMatches, nameMatches],
    limit
  );
};

export const resolveOrganizationObjectIds = async ({
  env,
  token,
  searchTerm,
  limit = 250,
}) => {
  const query = searchTerm.trim();

  if (!query) {
    return [];
  }

  const [exactMatch, objectIdMatches] = await Promise.all([
    fetchOrgById({ env, token, searchTerm: query }),
    fetchOrgsByObjectIdPrefix({ env, token, searchTerm: query, limit }),
  ]);

  return mergeOrganizations(
    [exactMatch ? [exactMatch] : [], objectIdMatches]
  ).map((org) => org.id);
};
